import { computed, ref } from "vue";
import { useStore } from "vuex";
import { luxService } from "@/modern/services/luxService";
import {
  LuxConfig,
  LuxContextType,
  LuxConversation,
  LuxPendingMessage,
} from "@/modern/types/lux";

/**
 * Estado do chat da Lux.
 *
 * A tela consome só o que está exposto aqui — nenhuma chamada de API acontece
 * dentro dos componentes.
 */
export function useLuxChat() {
  const store = useStore();

  const config = ref<LuxConfig | null>(null);
  const conversations = ref<LuxConversation[]>([]);
  const activeUuid = ref<string | null>(null);
  const messages = ref<LuxPendingMessage[]>([]);

  const isLoading = ref(false);
  const isSending = ref(false);
  const error = ref<string | null>(null);

  /** Idioma vem do seletor do topo, não do idioma em que o aluno digitou. */
  const language = computed<string>(
    () => store.getters.getCurrentLanguage || localStorage.getItem("currentLanguage") || "pt"
  );

  const activeConversation = computed(() =>
    conversations.value.find((conversation) => conversation.uuid === activeUuid.value) ?? null
  );

  const remaining = computed(() => config.value?.remaining_today ?? null);
  const reachedLimit = computed(() => remaining.value !== null && remaining.value <= 0);

  async function loadConfig(): Promise<void> {
    try {
      config.value = await luxService.fetchConfig();
    } catch (e: any) {
      // 402 = não é Elite. Quem trata é a tela, mostrando o upsell.
      if (e?.response?.status !== 402) {
        error.value = "Não foi possível carregar a Lux.";
      }
      throw e;
    }
  }

  async function loadConversations(): Promise<void> {
    conversations.value = await luxService.listConversations();
  }

  async function openConversation(uuid: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const conversation = await luxService.fetchConversation(uuid);
      activeUuid.value = conversation.uuid;
      messages.value = (conversation.messages ?? []) as LuxPendingMessage[];
    } catch (e) {
      error.value = "Não foi possível abrir esta conversa.";
    } finally {
      isLoading.value = false;
    }
  }

  async function startConversation(
    contextType: LuxContextType = "general",
    contextId: number | null = null
  ): Promise<void> {
    const conversation = await luxService.createConversation({
      context_type: contextType,
      context_id: contextId,
      language: language.value,
    });

    conversations.value = [conversation, ...conversations.value];
    activeUuid.value = conversation.uuid;
    messages.value = [];
  }

  /**
   * Envia a mensagem, já mostrando a fala do aluno na tela.
   *
   * Se a requisição falhar, a mensagem otimista é removida e o texto volta para
   * o campo — o aluno não perde o que escreveu.
   */
  async function sendMessage(content: string): Promise<string | null> {
    const text = content.trim();

    if (!text || isSending.value) {
      return null;
    }

    if (!activeUuid.value) {
      await startConversation();
    }

    const optimistic: LuxPendingMessage = {
      id: -Date.now(),
      role: "user",
      content: text,
      used_writeup: false,
      created_at: new Date().toISOString(),
      pending: true,
    };

    messages.value = [...messages.value, optimistic];
    isSending.value = true;
    error.value = null;

    try {
      const { message, usage } = await luxService.sendMessage(
        activeUuid.value as string,
        text,
        language.value
      );

      optimistic.pending = false;
      messages.value = [...messages.value, message as LuxPendingMessage];

      if (config.value && usage) {
        config.value = { ...config.value, ...usage };
      }

      // O título é gerado no backend na primeira troca; recarregar a lista
      // mantém a barra lateral coerente sem precisar de outro endpoint.
      await loadConversations();

      return null;
    } catch (e: any) {
      messages.value = messages.value.filter((m) => m.id !== optimistic.id);

      const status = e?.response?.status;

      if (status === 429) {
        error.value = e?.response?.data?.message ?? "Você atingiu o limite de mensagens de hoje.";
      } else if (status === 503) {
        error.value = e?.response?.data?.message ?? "A Lux está indisponível agora.";
      } else {
        error.value = "Não foi possível enviar a mensagem.";
      }

      // Devolve o texto para a tela recolocar no campo.
      return text;
    } finally {
      isSending.value = false;
    }
  }

  async function removeConversation(uuid: string): Promise<void> {
    await luxService.deleteConversation(uuid);

    conversations.value = conversations.value.filter((c) => c.uuid !== uuid);

    if (activeUuid.value === uuid) {
      activeUuid.value = null;
      messages.value = [];
    }
  }

  return {
    config,
    conversations,
    activeUuid,
    activeConversation,
    messages,
    isLoading,
    isSending,
    error,
    language,
    remaining,
    reachedLimit,
    loadConfig,
    loadConversations,
    openConversation,
    startConversation,
    sendMessage,
    removeConversation,
  };
}
